/**
 * Product Cockpit — the story view (beta, super-admin).
 *
 * One product, six steps. The SAME product card stays on screen and each step ADDS a layer
 * below it, so value appears rather than the page jumping around (Jaume, 2026-08-27).
 *
 * Colour contract: anything the cockpit COMPUTED or PHRASED is orange (`.pmc-syn`);
 * anything shown verbatim from the database is normal. Violet is reserved for model
 * opinion and arrives with the judge in PR4.
 *
 * What this PR deliberately does NOT show, because the read API does not carry it yet:
 * the recorded generation context and prompt structure (PR3, from S3) and the LLM judge
 * (PR4). Where they belong, the UI says they are not retrieved rather than staying silent.
 *
 * Story: #5636
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSite } from '@/contexts/SiteContext';
import {
  fetchProduct, type CockpitProduct, type CockpitIdentity, type StageName,
  type GenerationContext as GenCtx,
  type Judgement,
} from '@/services/productCockpitService';
import { DEFS, fmtInt } from './lib/stages';
import { merchantLedger, interpretTally } from './lib/checks';
import { StageDot } from './components/StageDot';
import { AttrRow } from './components/AttrRow';
import { SplitLedger } from './components/SplitLedger';
import { SectionContent } from './components/SectionContent';
import { GenerationContextPanel, NumberAuditLine, UnsourcedList, PromptStructure } from './components/GenerationContext';
import { JudgePanel, JudgeSectionBlock } from './components/JudgePanel';
import { TooltipHost } from './components/Tooltip';
import { Legend } from './components/Legend';
import './product-cockpit.css';

/** The six steps of the story. `match` is folded into Enrich/Interpret, as in the POC. */
const STEPS: Array<{ key: StageName; label: string }> = [
  { key: 'ingest', label: 'Ingest' },
  { key: 'enrich', label: 'Enrich' },
  { key: 'interpret', label: 'Interpret' },
  { key: 'generate', label: 'Generate' },
  { key: 'publish', label: 'Publish' },
  { key: 'perform', label: 'Perform' },
];

/** orange = computed by the cockpit, not read from the database */
const Syn = ({ children }: { children: React.ReactNode }) => (
  <span className="pmc-syn" data-tip="computed by the cockpit — not stored in the database">{children}</span>
);

function stageState(p: CockpitProduct, key: StageName) {
  return p.verdict?.stages?.find((s) => s.stage === key)?.state ?? 'unknown';
}

function isAbort(err: unknown): boolean {
  return (err as { name?: string })?.name === 'AbortError';
}

// ── is the category key one the cockpit could establish? ───────────────────
// ⛔ THE TWIN OF categoryKeyIsAuthoritative IN backend-merged/services/cockpit/judge.js, and
// deliberately keyed on the same two things: the resolution ERROR and the provenance marker,
// never on prose. The backend qualifies the judge's evidence with it; this qualifies what a
// human reads. Two consumers of one fact, so they must agree about it.
// Exported for tests: a page that silently drops this warning is the defect it exists for.
export function keyIsResolved(i: CockpitIdentity | null | undefined): boolean {
  if (!i) return true; // nothing to qualify yet — the panels below are not rendered either
  if (i.category_path_resolution_error) return false;
  // ⛔ THE RESOLVER'S OWN VERDICT FIRST. It ALWAYS returns a path — when the taxonomy does
  // not answer it hands its input back unchanged — so "the resolver ran" is not "there is a
  // key". Measured on the real resolver: 'Motorcycle Jackets' comes back as itself, flagged
  // no_slug, and reading only the provenance string called that resolved.
  if (i.category_key_unresolved === true) return false;
  // ⛔⛔ "IS THIS GENERATION'S KEY" IS ONE FACT, COMPUTED ONCE, AT THE SOURCE. Generation
  // resolves over the KNOWLEDGE-BASE ENTRY and never reads the feed's stored canonical path;
  // categoryKeyFor says whether the cockpit reproduced that, and this reads its answer. A
  // stored path, or a key computed from the feed because the product has no KBE, is NOT
  // generation's key however plausible it looks — round 65 promoted one by its prose.
  if (typeof i.category_key_is_generations === 'boolean') return i.category_key_is_generations;
  const from = String(i.category_path_source ?? '');
  // absent provenance is NOT a failure: a contract that predates the field says nothing about
  // the key, and shouting on every product would train a reader to scroll past the warning
  if (from === '') return true;
  return from.startsWith("generation's resolver");
}

// ── the one-line summary above each step ───────────────────────────────────
// exported for tests: the ratios it prints are the numbers a reader acts on, and one of
// them counted two different populations
export function stepSummary(p: CockpitProduct, step: number): string {
  const t1 = p.tab1_original ?? ({} as CockpitProduct['tab1_original']);
  const t2 = p.tab2_ai_enrichment ?? ({} as CockpitProduct['tab2_ai_enrichment']);
  const t4 = p.tab4_semantic ?? ({} as CockpitProduct['tab4_semantic']);
  const t8 = p.tab8_generation;

  switch (step) {
    case 1:
      return `the feed delivered ${t1.fields_used ?? '?'} fields`
        + (t1.fields_missing ? ` — ${t1.fields_missing} expected ones are absent` : '');
    case 2: {
      const n = (t2.attributes ?? []).length;
      const c = n ? merchantLedger(p).counts : null;
      // NB: summary.semantic_gaps is the count for the whole CATEGORY, not this product
      // (the contract's own gaps_note says "showing N of M queued gaps for this category").
      // Printing it beside per-product counts without saying so reads as if this one
      // product carried them all.
      const catGaps = t2.summary?.semantic_gaps ?? 0;
      // ⛔ the SAMPLE's length is not the count: both gap queries stop at 40, so with more
      // than 40 of this product's attributes carrying a gap the array understated the real
      // figure with nothing to say it had. The backend now counts without a LIMIT.
      const ownGaps = t2.gaps_for_attrs_total ?? (t2.gaps_for_attrs ?? []).length;
      return `+${n} attributes learned (category expects ${t2.coverage?.canonical_attributes_for_category ?? '?'})`
        + (c ? ` · ${c.exact} = from feed · ${c.normalized + c.derived} ~ normalised · ${c.new} + net new` : '')
        + (ownGaps ? ` · ${ownGaps} semantic gaps queued on this product's attributes` : '')
        + (catGaps ? ` (${fmtInt(catGaps)} across the category)` : '');
    }
    case 3: {
      const t = interpretTally(t2.attributes ?? [], t4.dictionary);
      const checked = t.ok + t.variant + t.unvalidated;
      return `the category defines ${t4.canonical_defined ?? '?'} of ${t4.canonical_total ?? '?'} attributes`
        + (checked ? ` · value check: ${t.ok} ✓${t.variant ? ` · ${t.variant} ≈` : ''}${t.unvalidated ? ` · ${t.unvalidated} ?` : ''}` : '')
        + (t.no_dictionary ? ` · ${t.no_dictionary} attrs unknown to the dictionary` : '')
        + (t.incoherent ? ` · ${t.incoherent} incoherent (hints, no definition)` : '')
        + ' — open a row for details';
    }
    case 4: {
      const n = (t8?.sections ?? []).length;
      return t8?.status
        ? `${n} section${n === 1 ? '' : 's'} · generated ${String(t8.generated_at ?? '').slice(0, 10)} · status ${t8.status}`
        : 'no generation exists for this product';
    }
    case 5: {
      // the aggregate total, never the capped display array — `urls` is LIMITed to 30
      // server-side and then sliced to 12 for display
      const u = p.tab10_publish?.urls_total ?? (p.tab10_publish?.urls ?? []).length;
      const s = p.tab9_translations?.summary;
      // ⛔ TWO POPULATIONS IN ONE RATIO. `current` counts TARGET translations; `total` counts
      // grid rows, which includes the SOURCE language. An English source with one current
      // French translation therefore read "1 of 2 languages current" — coverage looking
      // incomplete when every target was current. `targets` is the denominator that matches
      // the numerator, and "translations" is what is being counted, not "languages".
      const targets = s?.targets ?? null;
      return (u ? `${u} URL${u === 1 ? '' : 's'} observed live` : 'no live URL observed')
        + (targets ? ` · ${s?.current ?? 0} of ${targets} translations current` : '');
    }
    case 6: {
      const tt = p.tab12_search?.gsc?.totals_90d;
      return tt?.impressions
        ? `${fmtInt(tt.impressions)} impressions · ${tt.clicks ?? 0} clicks · avg position ${tt.avg_position ?? '—'} (recorded window)`
        : 'no search data recorded for this page';
    }
    default:
      return '';
  }
}

// ── steps 1–3: the persistent product card ─────────────────────────────────
function ProductCard({ product, step }: { product: CockpitProduct; step: number }) {
  const t1 = product.tab1_original;
  const t2 = product.tab2_ai_enrichment;
  const dictionary = product.tab4_semantic?.dictionary ?? null;
  const gaps = t2?.gaps_for_attrs ?? [];
  const enriched = t2?.attributes ?? [];
  const identity = product.identity;

  const feedRows = (t1?.fields ?? []).filter((f) => f.status === 'used');
  const missing = (t1?.fields ?? []).filter((f) => f.status === 'missing');
  const desc = feedRows.find((f) => f.key === 'description');
  const raws = t1?.raw_fields ?? [];

  const descBlock = desc ? (
    <details className="pmc-sdesc">
      <summary>
        <span className="pmc-sk">description</span>
        <span className="pmc-sval pmc-clamp">
          {String(desc.value).slice(0, 160)}{String(desc.value).length > 160 ? '…' : ''}
        </span>
      </summary>
      <div className="pmc-sval pmc-full">{String(desc.value)}</div>
    </details>
  ) : null;

  return (
    <div className="pmc-story-card">
      {identity.image_url
        ? <img className="pmc-story-img" src={identity.image_url} alt="" loading="lazy" />
        : <div className="pmc-story-img pmc-ph">{(identity.product_name_en ?? '?')[0]}</div>}

      <div className="pmc-story-attrs">
        {step === 1 || !enriched.length ? (
          <>
            <div
              className="pmc-sgroup-label"
              data-tip="Fields exactly as the merchant feed delivered them — nothing here was touched by AI."
            >
              from the feed
              {missing.length > 0 && <Syn>{` · ${missing.length} expected fields absent`}</Syn>}
            </div>
            {feedRows.filter((f) => f.key !== 'description').map((f) => (
              <AttrRow key={f.key} attrKey={f.key} value={f.value} />
            ))}
            {descBlock}
            {raws.length > 0 && (
              <details className="pmc-sdesc">
                <summary data-tip="The rest of the raw merchant feed row (additional_data), including its technical_specifications parsed as spec:<name>. Values here are feed data the mapped columns never carried.">
                  <span className="pmc-sk">raw feed data</span>
                  <span className="pmc-sval pmc-clamp">
                    {raws.length} more fields from the merchant feed
                  </span>
                </summary>
                <div className="pmc-leftovers">
                  {raws.map((r) => <AttrRow key={r.key} attrKey={r.key} value={r.value} />)}
                </div>
              </details>
            )}
            {step >= 2 && !enriched.length && (
              <>
                <div className="pmc-sgroup-label pmc-learned">learned by AI enrichment</div>
                <div className="pmc-srow">
                  <div className="pmc-srow-main">
                    {/*
                      ⛔ Two different stops in the pipeline, and this branch said one of them
                      whichever it was: an existing knowledge_base_entries row with `{}`
                      attributes read as "no enrichment record". Enrichment never running and
                      enrichment running and producing nothing need different fixes.
                    */}
                    <Syn>
                      {t2?.kbe_present
                        ? 'nothing — the enrichment record (knowledge_base_entries) exists but holds no extracted attributes'
                        : 'nothing — this product has no enrichment record (knowledge_base_entries)'}
                    </Syn>
                  </div>
                </div>
              </>
            )}
          </>
        ) : (
          <>
            <SplitLedger
              product={product}
              dictionary={step >= 3 ? dictionary : null}
              check={step >= 3}
              gaps={gaps}
            />
            {descBlock}
          </>
        )}
      </div>
    </div>
  );
}

const MiniCard = ({ product }: { product: CockpitProduct }) => {
  const i = product.identity;
  return (
    <div className="pmc-story-mini">
      {i.image_url && <img src={i.image_url} alt="" loading="lazy" />}
      <div>
        <div className="pmc-mname">{i.product_name_en ?? '(unnamed)'}</div>
        <div className="pmc-small pmc-mute">
          {[i.brand, i.site, i.price != null ? `${i.price} ${i.currency ?? ''}` : null]
            .filter(Boolean).join(' · ')}
        </div>
      </div>
    </div>
  );
};

const Stat = ({ value, label }: { value: React.ReactNode; label: string }) => (
  <div className="pmc-stat"><div className="pmc-sv">{value}</div><div className="pmc-sl">{label}</div></div>
);

// ── step 4: the generated page ─────────────────────────────────────────────
// exported for tests: the empty-sections branch has no network of its own, and it is the
// branch that used to name the wrong pipeline stage
export function GenerateStep({ product, siteId, productRef }: { product: CockpitProduct; siteId: string; productRef: string }) {
  const t8 = product.tab8_generation;
  const sections = t8?.sections ?? [];
  const [open, setOpen] = useState<string | null>(null);
  // the recorded context arrives from its own endpoint (S3); keep it here so each section
  // panel can show its own number audit and prompt structure
  const [ctx, setCtx] = useState<GenCtx | null>(null);
  const onLoaded = useCallback((c: GenCtx | null) => setCtx(c), []);
  // S3 keys prompts by family, aeo_content by section name. Match either, or the audits and
  // prompt structure silently vanish for 8 of 10 families (the judge had the same bug).
  const ctxSection = (name: string) => ctx?.sections.find(
    (x) => x.section_name === name || x.name === name,
  ) ?? null;
  const [judgement, setJudgement] = useState<Judgement | null>(null);
  const onJudged = useCallback((j: Judgement | null) => setJudgement(j), []);

  // ⛔ "no sections" and "no row" are DIFFERENT places for the chain to stop, and only the
  // second one is what this used to say. A generation that exists but failed, is still
  // pending, or produced nothing has an apg_id and a recorded status while its sections
  // array is empty — reporting it as a missing row named the wrong stage AND threw away the
  // status, id and timestamp that say what actually happened.
  if (!sections.length) {
    const hasRow = t8?.apg_id != null;
    return (
      <div className="pmc-story-empty">
        {hasRow
          ? (
            <>
              <Syn>
                {`An aeo_page_generations row exists (status ${t8?.status ?? 'unknown'}) but it carries no sections — the chain stops inside generation, not before it.`}
              </Syn>
              <div className="pmc-small pmc-mute pmc-genmeta">
                <span className="pmc-mono">{t8?.apg_id}</span>
                {t8?.model && <span>{` · ${t8.model}`}</span>}
                {t8?.cost_usd != null && <span>{` · $${t8.cost_usd}`}</span>}
                {t8?.generated_at && <span>{` · created ${String(t8.generated_at).slice(0, 10)}`}</span>}
              </div>
            </>
          )
          : <Syn>No aeo_page_generations row — the chain stops before this step.</Syn>}
      </div>
    );
  }

  const section = sections.find((s) => s.name === open);

  return (
    <>
      <div className="pmc-small pmc-mute pmc-genmeta">
        <span className="pmc-mono">{t8?.model ?? ''}</span>
        {t8?.cost_usd != null && <span>{` · $${t8.cost_usd}`}</span>}
        {t8?.generated_at && <span>{` · generated ${String(t8.generated_at).slice(0, 10)}`}</span>}
      </div>

      <GenerationContextPanel siteId={siteId} productRef={productRef} onLoaded={onLoaded} />

      <JudgePanel siteId={siteId} productRef={productRef} onLoaded={onJudged} />

      <div className="pmc-sgroup-label">the generated page — click a section to read it</div>
      <div className="pmc-secchips">
        {sections.map((s) => (
          <button
            type="button"
            key={s.name}
            className={`pmc-secchip${open === s.name ? ' pmc-active' : ''}`}
            aria-pressed={open === s.name}
            data-tip={`${s.name.replace(/_/g, ' ')} — click to read this generated section rendered like the live page${s.deterministic ? ' (deterministic data section)' : ''}`}
            onClick={() => setOpen((cur) => (cur === s.name ? null : s.name))}
          >
            {s.name.replace(/_/g, ' ')}
            {s.deterministic && <span className="pmc-secchip-dot pmc-data" />}
            {!!ctxSection(s.name)?.number_audit?.unsourced.length && (
              <span className="pmc-secchip-dot pmc-warn" />
            )}
            {judgement?.verdicts?.[s.name]?.verdict === 'major_gaps' && (
              <span className="pmc-secchip-dot pmc-judgedot" />
            )}
          </button>
        ))}
      </div>

      {section && (
        <div className="pmc-secpanel">
          <div className="pmc-secpanel-hd">
            <b>{section.name.replace(/_/g, ' ')}</b>
            {section.deterministic && (
              <span className="pmc-sbadge" data-tip="data — a deterministic section: built directly from stored data, the AI does not write free text here.">data</span>
            )}
            {section.updated_at && (
              <span className="pmc-small pmc-mute pmc-push">updated {String(section.updated_at).slice(0, 10)}</span>
            )}
          </div>
          <SectionContent
            name={section.name}
            contentJson={section.content_json}
            flatText={section.content_es}
            truncated={section.content_json_truncated}
          />
          {ctxSection(section.name)?.number_audit && (
            <>
              <NumberAuditLine audit={ctxSection(section.name)!.number_audit!} />
              <UnsourcedList audit={ctxSection(section.name)!.number_audit!} />
            </>
          )}
          {judgement?.verdicts?.[section.name] && (
            <JudgeSectionBlock section={judgement.verdicts[section.name]} />
          )}
          {ctxSection(section.name) && <PromptStructure section={ctxSection(section.name)!} />}
          {section.content_json_truncated && (
            <div className="pmc-small">
              <Syn>
                {section.content_json_truncated.kept
                  ? `showing ${section.content_json_truncated.kept} of ${section.content_json_truncated.total} items — the full section is ${fmtInt(section.content_json_truncated.full_chars ?? 0)} chars in the DB`
                  : section.content_json_truncated.note ?? 'section truncated'}
              </Syn>
            </div>
          )}
        </div>
      )}
    </>
  );
}

// ── steps 5 and 6 ──────────────────────────────────────────────────────────
function PublishStep({ product }: { product: CockpitProduct }) {
  const urls = product.tab10_publish?.urls ?? [];
  const grid = product.tab9_translations?.grid ?? [];
  return (
    <>
      {urls.length ? (
        <>
          <div className="pmc-sgroup-label">
            live URLs (publisher manifests)
            {(product.tab10_publish?.urls_total ?? 0) > urls.slice(0, 12).length && (
              <Syn>{` · showing ${Math.min(urls.length, 12)} of ${product.tab10_publish?.urls_total}`}</Syn>
            )}
          </div>
          <div>
            {urls.slice(0, 12).map((u) => (
              <div className="pmc-srow" key={u.url}>
                <div className="pmc-srow-main">
                  <span className="pmc-sk">{u.country_code ?? u.lang ?? '—'}</span>
                  <a className="pmc-sval pmc-mono pmc-small" href={u.url} target="_blank" rel="noopener noreferrer">{u.url}</a>
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <div className="pmc-story-empty">
          <Syn>
            {product.identity.apg_status === 'published'
              ? 'published in the database, but no manifest row observed this URL live'
              : 'nothing published yet — expected at this product’s stage'}
          </Syn>
        </div>
      )}
      {grid.length > 0 && (
        <>
          <div className="pmc-sgroup-label">languages</div>
          <div className="pmc-slangs">
            {grid.map((l) => (
              <span className={`pmc-slang pmc-l-${l.status}`} key={l.lang} data-tip={`status: ${l.status}`}>{l.lang}</span>
            ))}
          </div>
        </>
      )}
    </>
  );
}

// exported for tests: the totals carry no recency filter, so the window shown with them
// is what stops an old collector window from reading as current performance
export function PerformStep({ product }: { product: CockpitProduct }) {
  const tt = product.tab12_search?.gsc?.totals_90d;
  // The backend aggregates ALL recorded GSC history with no recency filter and reports the
  // window it actually covered. Showing the totals without it let a collector window that
  // ended months ago read as current performance — the number is right and the reader's
  // conclusion is wrong. `None → None` is the backend's own spelling for "no dates".
  const gscWindow = product.tab12_search?.gsc?.window;
  const hasWindow = typeof gscWindow === 'string' && gscWindow.length > 0 && gscWindow !== 'None → None';
  const queries = (product.tab12_search?.queries ?? []).slice(0, 6);
  const aiBots = (product.tab11_robots?.bots ?? []).filter((b) => b.is_ai_crawler);
  return (
    <>
      {tt?.impressions ? (
        <>
          <div className="pmc-sstats">
            <Stat value={fmtInt(tt.impressions)} label="impressions" />
            <Stat value={fmtInt(tt.clicks ?? 0)} label="clicks" />
            <Stat value={tt.avg_position ?? '—'} label="avg position" />
          </div>
          <div
            className="pmc-small pmc-mute pmc-gscwindow"
            data-tip="every GSC row ever recorded for this page is aggregated — there is no recency filter, so these totals describe THIS window, not the last 90 days"
          >
            {hasWindow
              ? `measured over the full recorded GSC window: ${gscWindow}`
              : 'the recorded GSC window is not reported for these totals — they cover all recorded history, whenever that was'}
          </div>
          {queries.length > 0 && (
            <>
              <div className="pmc-sgroup-label">top search queries (GSC)</div>
              <div className="pmc-tablewrap">
                <table className="pmc-data">
                  <thead>
                    <tr>
                      <th>query</th><th className="pmc-num">impressions</th>
                      <th className="pmc-num">clicks</th><th className="pmc-num">position</th>
                    </tr>
                  </thead>
                  <tbody>
                    {queries.map((q) => (
                      <tr key={q.query}>
                        <td>{q.query}</td>
                        <td className="pmc-num">{q.volume != null ? fmtInt(q.volume) : '—'}</td>
                        <td className="pmc-num">{q.clicks ?? 0}</td>
                        <td className="pmc-num">{q.position ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      ) : (
        <div className="pmc-story-empty">
          <Syn>
            no gsc_performance_data rows matched this page — often a pre-publication state or
            a collector window gap, not proof of silence
          </Syn>
        </div>
      )}
      {aiBots.length > 0 && (
        <>
          <div className="pmc-sgroup-label">AI crawlers seen on this page</div>
          <div className="pmc-slangs">
            {aiBots.slice(0, 8).map((b) => (
              <span className="pmc-slang pmc-l-current" key={b.bot} data-tip={`last seen ${b.last_seen} · ${b.visits_90d} visits recorded`}>{b.bot}</span>
            ))}
          </div>
        </>
      )}
    </>
  );
}

// ── page ───────────────────────────────────────────────────────────────────
export default function ProductCockpitStory() {
  const { ref } = useParams<{ ref: string }>();
  const { selectedSite, loading: siteLoading } = useSite();
  const navigate = useNavigate();
  const siteId = selectedSite?.id ?? null;

  const [product, setProduct] = useState<CockpitProduct | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<{ message: string; code: string } | null>(null);
  const [step, setStep] = useState(1);

  useEffect(() => {
    if (!siteId || !ref) return undefined;
    const ac = new AbortController();
    setLoading(true);
    setError(null);
    fetchProduct(siteId, ref, ac.signal)
      .then((data) => { setProduct(data); setLoading(false); })
      .catch((err) => {
        if (isAbort(err)) return;
        setError({ message: err?.message ?? String(err), code: err?.code ?? 'error' });
        setLoading(false);
      });
    return () => ac.abort();
  }, [siteId, ref]);

  const body = useMemo(() => {
    if (!product) return null;
    if (step <= 3) return <ProductCard product={product} step={step} />;
    return (
      <div>
        <MiniCard product={product} />
        {step === 4 && <GenerateStep product={product} siteId={siteId!} productRef={ref!} />}
        {step === 5 && <PublishStep product={product} />}
        {step === 6 && <PerformStep product={product} />}
      </div>
    );
  }, [product, step]);

  const back = useCallback(() => navigate('/product-cockpit'), [navigate]);

  if (siteLoading) return <div className="pm-cockpit"><div className="pmc-empty">Loading sites…</div></div>;

  if (!siteId) {
    return (
      <div className="pm-cockpit">
        <div className="pmc-empty">Select a site in the header to inspect this product.</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pm-cockpit">
        <button type="button" className="pmc-btn" onClick={back}>‹ back to the fleet</button>
        <div className="pmc-error" style={{ marginTop: 14 }}>
          <div className="pmc-error-title">
            {error.code === 'not_found'
              ? `No product matches “${ref}” on ${selectedSite?.name}`
              : error.code === 'ambiguous'
                ? `“${ref}” matches several products`
                : 'This product could not be loaded'}
          </div>
          <div className="pmc-error-msg">{error.message}</div>
        </div>
      </div>
    );
  }

  if (loading || !product) {
    return (
      <div className="pm-cockpit">
        <div className="pmc-skel" style={{ width: 280, height: 24 }} />
        <div className="pmc-skel" style={{ width: '60%', marginTop: 10 }} />
        <div className="pmc-skel" style={{ width: '100%', height: 220, marginTop: 20 }} />
      </div>
    );
  }

  const i = product.identity;

  return (
    <div className="pm-cockpit">
      <button type="button" className="pmc-btn" onClick={back}>‹ back to the fleet</button>

      <div className="pmc-story-head">
        <h1 className="pmc-h1">{i.product_name_en ?? '(unnamed)'}</h1>
        <div className="pmc-small pmc-mute">
          {[i.brand ?? '—', i.site ?? '—', i.price != null ? `${i.price} ${i.currency ?? ''}` : null]
            .filter(Boolean).join(' · ')}
          {' · '}
          <span
            className="pmc-mono pmc-small"
            title={i.category_path_source ? `category key from ${i.category_path_source}` : undefined}
          >
            {i.category_path ?? ''}
          </span>
        </div>
        <div className="pmc-syn pmc-small pmc-contract">
          ◆ orange = computed by the cockpit · everything else is shown verbatim from the database
        </div>
      </div>

      {/*
        ⛔ THE KEY IS NOT ALWAYS A KEY, AND THE PAGE MUST SAY SO. Generation resolves the feed
        category before any category read; when this cockpit's copy of that resolver refuses,
        or returns nothing, the raw feed category is used as a STAND-IN so the page can still
        be built — and every panel below keyed on it (the dictionary, the benefit pool, the
        personas, the gaps) may then be about a different category. The provenance was already
        in the contract and rendered NOWHERE, which is the same defect as not having it.
      */}
      {!keyIsResolved(i) && (
        <div className="pmc-error" style={{ marginBottom: 12 }}>
          <div className="pmc-error-title">
            This product&apos;s category key is not the one generation used
          </div>
          <div className="pmc-error-msg">
            {`Used as the key: "${i.category_path ?? ''}" (${i.category_path_source ?? 'provenance unknown'}). `}
            {i.category_path_resolution_error
              ? `The resolver refused: ${i.category_path_resolution_error}. ` : ''}
            {i.category_key_unresolved && i.category_key_unresolved_reason
              ? `The taxonomy did not answer: ${i.category_key_unresolved_reason}. ` : ''}
            {i.category_key_from_kbe === false
              ? 'This product has no knowledge-base entry, so generation has never resolved it and the key was computed from the feed. ' : ''}
            {i.stored_canonical_category_path && i.stored_canonical_category_path !== i.category_path
              ? `The feed also stores a canonical path, "${i.stored_canonical_category_path}" — generation never reads it. ` : ''}
            Generation resolves its category over the knowledge-base entry before any
            category-keyed read, so the dictionary, benefits, personas and gaps below were
            fetched with a key generation may never have used. They may describe a different
            category. Nothing here is evidence that this product&apos;s category lacks an
            attribute, a benefit or a persona.
          </div>
        </div>
      )}

      <div className="pmc-stepper">
        {STEPS.map((s, idx) => {
          const n = idx + 1;
          return (
            <React.Fragment key={s.key}>
              <button
                type="button"
                className={`pmc-step${step === n ? ' pmc-active' : ''}${step > n ? ' pmc-done' : ''}`}
                data-tip={DEFS[s.key]}
                aria-current={step === n}
                onClick={() => setStep(n)}
              >
                <span className="pmc-sno">{n}</span>
                <span className="pmc-slabel">{s.label}</span>
                <StageDot stage={s.key} state={stageState(product, s.key)} />
              </button>
              {n < STEPS.length && <span className="pmc-sarrow" aria-hidden="true">→</span>}
            </React.Fragment>
          );
        })}
      </div>

      <div className="pmc-story-summary"><Syn>{stepSummary(product, step)}</Syn></div>

      {body}

      <Legend />
      <TooltipHost />
    </div>
  );
}
